"use client";

/**
 * Stat line above the history cards: "N changes since initial setup ·
 * N contributors ⌄ · last change 2h ago". The contributors button expands an
 * inline chip list. Ported from the design's `.d-statline` / `.stat-contrib`.
 */

import { useId, useState } from "react";
import { formatRelativeTime } from "../lib/time-format";
import {
  avatarColorClass,
  avatarForegroundClass,
  avatarInitials,
} from "./AvatarStack";

export interface HistoryStatLineProps {
  changeCount: number;
  contributorNames: string[];
  /** ISO timestamp of the most recent change; null when there are none. */
  lastChangeAt: string | null;
}

export function HistoryStatLine({
  changeCount,
  contributorNames,
  lastChangeAt,
}: HistoryStatLineProps) {
  const [open, setOpen] = useState(false);
  const contributorsId = useId();

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-[13px] text-(--fg-secondary)">
      <span>
        <b className="font-bold text-(--fg-primary)">{changeCount}</b>{" "}
        {changeCount === 1 ? "change" : "changes"} since initial setup
      </span>
      {contributorNames.length > 0 ? (
        <>
          <span className="text-(--fg-subtle)">·</span>
          <button
            type="button"
            className={[
              "inline-flex cursor-pointer items-center gap-1 rounded-sm border-0 bg-transparent p-0",
              "font-[inherit] text-[13px] text-(--fg-secondary)",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)",
            ].join(" ")}
            aria-controls={open ? contributorsId : undefined}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            <b className="font-bold text-(--fg-primary)">
              {contributorNames.length}
            </b>{" "}
            {contributorNames.length === 1 ? "contributor" : "contributors"}
            <span
              aria-hidden
              className={[
                "text-(--fg-muted) transition-transform",
                open ? "rotate-180" : "",
              ].join(" ")}
            >
              ⌄
            </span>
          </button>
        </>
      ) : null}
      {lastChangeAt ? (
        <span className="ml-auto text-[11.5px] text-(--fg-muted)">
          last change {formatRelativeTime(lastChangeAt)}
        </span>
      ) : null}
      {open && contributorNames.length > 0 ? (
        <ul
          id={contributorsId}
          className="m-0 flex basis-full list-none flex-wrap gap-2 p-0 pt-2"
          aria-label="Contributors"
        >
          {contributorNames.map((name, index) => (
            <li
              key={`${name}-${index}`}
              className={[
                "inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full",
                "border border-(--border-subtle) bg-(--bg-white) py-1 pr-2.5 pl-1",
                "text-[12px] text-(--fg-secondary)",
              ].join(" ")}
            >
              <span
                className={[
                  "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full",
                  "text-[8.5px] font-bold",
                  avatarColorClass(name),
                  avatarForegroundClass(name),
                ].join(" ")}
                aria-hidden
              >
                {avatarInitials(name)}
              </span>
              <span className="min-w-0 break-words">{name}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
